import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FooterColumnType,
  FooterSocialSite,
  IFooter,
  IFooterSocialLink,
  MerchandiseService,
} from '@epicuro-next/data/merchandise';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faPinterest,
  faTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

const BREAKPOINT = '(max-width: 767px)';

@Component({
  selector: 'epc-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FooterComponent implements OnInit, OnDestroy {
  public footerContent$: Observable<IFooter | null>;

  public footerColumnType = FooterColumnType;
  public socialLinkIcons = {
    [FooterSocialSite.FACEBOOK]: faFacebook,
    [FooterSocialSite.INSTAGRAM]: faInstagram,
    [FooterSocialSite.YOUTUBE]: faYoutube,
    [FooterSocialSite.TWITTER]: faTwitter,
    [FooterSocialSite.LINKEDIN]: faLinkedin,
    [FooterSocialSite.PINTEREST]: faPinterest,
  };

  public showAccordion$ = this.breakpointObserver
    .observe(BREAKPOINT)
    .pipe(map((result) => result.matches));

  public thisYear = new Date().getFullYear();

  private destroy$ = new Subject<void>();

  constructor(
    private breakpointObserver: BreakpointObserver,
    private store: Store,
    private merchandiseService: MerchandiseService,
  ) {}

  public ngOnInit() {
    this.footerContent$ = combineLatest([
      this.store.select(selectSiteId),
      this.store.select(selectAccountId).pipe(filter<string>(Boolean)),
    ]).pipe(
      takeUntil(this.destroy$),
      map(([siteId, accountId]) => ({ siteId, accountId })),
      distinctUntilChanged(),
      switchMap(({ siteId, accountId }) =>
        this.merchandiseService
          .getFooter(siteId, accountId)
          .pipe(catchError(() => of(null))),
      ),
    );
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public socialLinkHref(link: IFooterSocialLink): string {
    return `https://www.${link.site}.com/${link.name}`;
  }

  public isSameDomain(link: string = '') {
    return link?.startsWith('http')
      ? link.includes(window?.location.hostname)
      : true;
  }
}
